import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';
import {
  documentPublishRequestService,
  PendingDocumentPublishRequestDto,
} from '../../services/documentPublishRequestService';
import { documentService, DocumentPickerNode } from '../../services/documentService';
import { formatBytes } from '../../utils/formatBytes';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

const DocumentPublishQueuePage: React.FC = () => {
  const { addToast } = useToast();
  const [rows, setRows] = useState<PendingDocumentPublishRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewRow, setReviewRow] = useState<PendingDocumentPublishRequestDto | null>(null);
  const [approveMode, setApproveMode] = useState(true);
  const [folders, setFolders] = useState<DocumentPickerNode[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await documentPublishRequestService.listPending();
      setRows(list);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao carregar solicitações de publicação.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!reviewRow || !approveMode) return;
    let cancelled = false;
    setFoldersLoading(true);
    documentService
      .getFolderPickerNodes()
      .then(nodes => {
        if (!cancelled) setFolders(nodes);
      })
      .catch(() => {
        if (!cancelled) {
          addToast('Erro ao carregar pastas.', 'error');
          setFolders([]);
        }
      })
      .finally(() => {
        if (!cancelled) setFoldersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewRow, approveMode, addToast]);

  const openReview = (row: PendingDocumentPublishRequestDto, approve: boolean) => {
    setReviewRow(row);
    setApproveMode(approve);
    setTargetFolderId(row.targetDocumentId ?? null);
    setReviewNotes('');
    setFolderSearch('');
  };

  const filteredFolders = useMemo(() => {
    const q = folderSearch.trim().toLowerCase();
    if (q.length < 2) return [];
    return folders
      .filter(
        f =>
          f.title.toLowerCase().includes(q) ||
          f.fullPath.toLowerCase().includes(q) ||
          f.path.toLowerCase().includes(q)
      )
      .slice(0, 100);
  }, [folders, folderSearch]);

  const submitReview = async () => {
    if (!reviewRow) return;
    if (!approveMode && !reviewNotes.trim()) {
      addToast('Informe o motivo da rejeição.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await documentPublishRequestService.review(reviewRow.id, {
        approve: approveMode,
        targetDocumentId: approveMode ? targetFolderId ?? undefined : undefined,
        notes: reviewNotes.trim() || undefined,
      });
      addToast(approveMode ? 'Solicitação aprovada.' : 'Solicitação rejeitada.', 'success');
      setReviewRow(null);
      await load();
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Não foi possível concluir a revisão.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Solicitações de publicação</h1>
          <p className="text-sm text-gray-600 mt-1">
            Aprove ou rejeite pedidos para publicar documentos privados na árvore corporativa.
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={() => void load()} disabled={loading}>
          Atualizar
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-500">Nenhuma solicitação pendente.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Arquivo</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Solicitante</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Data</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Notas</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900">{r.privateDocument.fileName}</div>
                    <div className="text-xs text-gray-500">
                      {r.privateDocument.fileType} · {formatBytes(r.privateDocument.fileSizeBytes)}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    <div>{r.requestedBy.name}</div>
                    <div className="text-xs text-gray-500">{r.requestedBy.email}</div>
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {new Date(r.createdAt).toLocaleString('pt-BR')}
                  </td>
                  <td className="py-3 px-4 text-gray-600 max-w-xs truncate" title={r.notes || ''}>
                    {r.notes || '—'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => openReview(r, true)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </Button>
                      <Button type="button" variant="danger" size="sm" onClick={() => openReview(r, false)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reviewRow && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {approveMode ? 'Aprovar publicação' : 'Rejeitar solicitação'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{reviewRow.privateDocument.fileName}</strong> — {reviewRow.requestedBy.name}
            </p>
            {approveMode && (
              <>
                <p className="text-sm text-gray-700 mb-2">Pasta de destino (opcional; busque abaixo)</p>
                <input
                  type="search"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 mb-2 text-sm"
                  placeholder="Buscar pasta (mín. 2 caracteres)..."
                  value={folderSearch}
                  onChange={e => setFolderSearch(e.target.value)}
                />
                {foldersLoading && <p className="text-sm text-gray-500 mb-2">Carregando pastas...</p>}
                <ul className="border border-gray-100 rounded-md max-h-36 overflow-y-auto mb-4 divide-y">
                  {filteredFolders.map(f => (
                    <li key={f.id}>
                      <button
                        type="button"
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                          targetFolderId === f.id ? 'bg-blue-50 text-blue-800' : ''
                        }`}
                        onClick={() => setTargetFolderId(f.id === targetFolderId ? null : f.id)}
                      >
                        <div className="font-medium">{f.title}</div>
                        <div className="text-xs text-gray-500 truncate">{f.fullPath || f.path}</div>
                      </button>
                    </li>
                  ))}
                </ul>
                {targetFolderId && (
                  <p className="text-xs text-gray-600 mb-2">Pasta selecionada para destino.</p>
                )}
              </>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {approveMode ? 'Observações (opcional)' : 'Motivo da rejeição'}
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 text-sm"
              rows={3}
              value={reviewNotes}
              onChange={e => setReviewNotes(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setReviewRow(null)}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant={approveMode ? 'primary' : 'danger'}
                disabled={submitting}
                onClick={() => void submitReview()}
              >
                {submitting ? 'Enviando...' : approveMode ? 'Aprovar' : 'Rejeitar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPublishQueuePage;
